import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { EASE, Reveal } from "@/components/motion";
import ProductCard from "@/components/ProductCard";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { catSlug as slug, orderedCategories, BASE_CATEGORIES, CATEGORY_SEO } from "@/lib/categories";

const Catalog = () => {
  useSeo({
    title: "Boutique — Électricité, Plomberie, Meubles, Décoration",
    description:
      "Tout le catalogue Sama-Keur : électricité, plomberie, meubles et décoration. Prix en FCFA, livraison à Dakar et dans les régions du Sénégal, paiement Wave, Orange Money ou carte bancaire.",
    keywords:
      "meubles Dakar, robinetterie Dakar, mitigeur Sénégal, salle de bain moderne Dakar, cuisine équipée Sénégal, boutique maison Dakar",
    path: "/boutique",
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [tabCats, setTabCats] = useState(BASE_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("pertinence");
  const [closedCats, setClosedCats] = useState(new Set());
  const category = searchParams.get("categorie") || "Tout";

  useEffect(() => {
    setLoading(true);
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api
      .get("/categories")
      .then((r) => {
        if (r.data?.length) setTabCats(r.data.map((c) => c.name));
      })
      .catch(() => {});
  }, []);

  const cats = useMemo(() => orderedCategories(products), [products]);

  const toggleCat = (c) =>
    setClosedCats((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  const groups = useMemo(() => {
    const visible = category === "Tout" ? cats : [category];
    return visible
      .map((cat) => {
        let items = products.filter((p) => p.category === cat);
        if (sort === "prix-asc") items = [...items].sort((a, b) => a.price - b.price);
        if (sort === "prix-desc") items = [...items].sort((a, b) => b.price - a.price);
        return { cat, items };
      })
      .filter((g) => g.items.length > 0);
  }, [products, category, sort, cats]);

  const totalShown = groups.reduce((s, g) => s + g.items.length, 0);

  return (
    <main data-testid="catalog-page" className="">
      <div className="border-b border-clay">
        <div className="mx-auto max-w-7xl px-6 pb-10 pt-16 lg:px-10">
          <Reveal y={24}>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Catalogue</p>
            <h1 className="mt-3 font-display text-4xl font-bold tracking-tighter text-bark sm:text-5xl md:text-6xl">
              La Boutique
            </h1>
            <p className="mt-4 max-w-lg text-bark/60">
              {totalShown} pièce{totalShown > 1 ? "s" : ""} répartie{totalShown > 1 ? "s" : ""} en {groups.length} univers — livraison à domicile à Dakar, Thiès, Mbour & Saint-Louis.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <div className="mb-12 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {["Tout", ...tabCats].map((c) => (
              <button
                key={c}
                data-testid={`category-tab-${slug(c)}`}
                onClick={() => setSearchParams(c === "Tout" ? {} : { categorie: c })}
                className={`h-11 rounded-full border px-5 text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                  category === c
                    ? "border-terracotta bg-terracotta text-sand"
                    : "border-clay bg-cream text-bark/60 hover:border-terracotta hover:text-terracotta"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-11 rounded-full border border-clay bg-cream px-5 text-xs font-bold uppercase tracking-[0.15em] text-bark/70 focus:border-terracotta focus:outline-none"
          >
            <option value="pertinence">Pertinence</option>
            <option value="prix-asc">Prix croissant</option>
            <option value="prix-desc">Prix décroissant</option>
          </select>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 animate-pulse border border-clay bg-clay/40" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <p data-testid="catalog-empty" className="py-24 text-center font-display text-2xl text-bark/50">
            Aucun produit dans cette catégorie pour le moment.
          </p>
        ) : (
          <div data-testid="catalog-groups" className="space-y-4 pb-24">
            {groups.map((g, gi) => {
              const open = category !== "Tout" || !closedCats.has(g.cat);
              return (
                <Reveal key={g.cat} delay={gi * 0.05}>
                  <section className="border border-clay bg-cream">
                    <button
                      data-testid={`catalog-group-${slug(g.cat)}`}
                      onClick={() => toggleCat(g.cat)}
                      className="flex w-full items-center justify-between gap-4 px-6 py-6 text-left"
                    >
                      <div className="flex items-center gap-5">
                        <span className="font-display text-4xl font-bold leading-none text-terracotta/25">{String(gi + 1).padStart(2, "0")}</span>
                        <div>
                          <h2 className="font-display text-2xl font-semibold tracking-tight text-bark md:text-3xl">{g.cat}</h2>
                          <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-bark/40">
                            {g.items.length} produit{g.items.length > 1 ? "s" : ""}
                          </p>
                          {CATEGORY_SEO[g.cat] && (
                            <p className="mt-2 max-w-xl text-xs leading-relaxed text-bark/55 normal-case tracking-normal">
                              {CATEGORY_SEO[g.cat]}
                            </p>
                          )}
                        </div>
                      </div>
                      <span
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                          open ? "rotate-180 border-terracotta text-terracotta" : "border-clay text-bark/50"
                        }`}
                      >
                        <ChevronDown className="h-5 w-5" />
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.5, ease: EASE }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-1 gap-6 border-t border-clay p-6 sm:grid-cols-2 lg:grid-cols-3">
                            {g.items.map((p, i) => (
                              <ProductCard key={p.id} product={p} index={i} />
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </section>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

export default Catalog;
